import CustomPaper from '../../containers/Paper'
import { UserDTO } from '../../models/users'
import CustomButton from '../../components/Button'
import EditIcon from '@mui/icons-material/Edit'
import EmailIcon from '@mui/icons-material/Email'
import { useContext, useState } from 'react'
import CustomModal from '../../containers/Modal'
import UpdateUserInfo from './UpdateUserInfo'
import { isAdmin } from '../../helpers/utils'
import { useNavigate } from 'react-router-dom'
import { AppRoutes } from '../../helpers/routes'
import LinkedInIcon from '@mui/icons-material/LinkedIn'
import FileDownloadIcon from '@mui/icons-material/FileDownload'
import { PDFDownloadLink } from '@react-pdf/renderer'
import PDF from '../job/PDF'
import { AppConfig } from '../../models/shared'
import { PageContext } from '../../context/PageContext'

interface Props {
    user: UserDTO
    self: boolean
    config?: AppConfig
}
const UserBasicInfo = ({ user, self, config }: Props) => {
    const [isUpdateInfoOpen, setIsUpdateInfoOpen] = useState<boolean>(false)
    const navigate = useNavigate()
    const { smallScreen } = useContext(PageContext)

    const getLastJob = () =>
        user.workHistories.find((x) => x.currentJob) ??
        user.workHistories
            .sort((x, y) => new Date(x.endDate).getTime() - new Date(y.endDate).getTime())
            .at(0)!

    return (
        <CustomPaper
            className={`h-fit md:h-[208px] flex pb-3 ${isAdmin() && 'pb-0'} md:pb-0 p-0 overflow-hidden text-primary relative`}
        >
            <div className="rounded-l-default aspect-square h-24 md:h-48 border-solid border-[4px] md:border-[8px] border-white shadow-default">
                <img
                    alt="userImage"
                    src={user.picture && user.picture !== '' ? user.picture : '/adminPhoto.png'}
                    className="rounded-l-[14px]"
                    width="100%"
                    height="100%"
                />
                {smallScreen
                    ? !isAdmin() && (
                          <div className="w-fit">
                              {self ? (
                                  <div className="flex flex-col gap-1">
                                      <CustomButton
                                          startIcon={<EditIcon />}
                                          text="Actualizar"
                                          onClick={() => setIsUpdateInfoOpen(true)}
                                          secondary
                                          className="text-xs w-28 mt-1"
                                      />
                                      <PDFDownloadLink
                                          document={<PDF user={user} />}
                                          fileName="profile.pdf"
                                      >
                                          <CustomButton
                                              startIcon={<FileDownloadIcon />}
                                              text="Descargar"
                                              secondary
                                              className="text-xs w-28 rounded-l-[14px]"
                                          />
                                      </PDFDownloadLink>
                                  </div>
                              ) : (
                                  <CustomButton
                                      startIcon={<EmailIcon />}
                                      text="Enviar mensaje"
                                      onClick={() =>
                                          navigate(AppRoutes.messages.replace(':userId', user.id), {
                                              state: { messageTo: user },
                                          })
                                      }
                                      secondary
                                      className="text-xs w-28 mt-1"
                                  />
                              )}
                          </div>
                      )
                    : null}
            </div>
            <div className="flex gap-1 md:gap-2 w-full justify-around">
                <div className="flex flex-col md:flex-row gap-2 md:gap-0 text-lg md:text-2xl justify-between min-w-[70%] max-w-[85%] w-full">
                    <div className="flex flex-col justify-center md:gap-4 h-full text-base md:text-xl w-full md:w-[50%]">
                        <span
                            className="font-bold text-lg md:text-3xl line-clamp-2"
                            title={user.firstName + ' ' + user.lastName}
                        >
                            {user.firstName + ' ' + user.lastName}
                        </span>
                        <div className="flex flex-col gap-1">
                            <span
                                title={getLastJob()?.jobTitle}
                                className="line-clamp-1 text-sm md:text-2xl"
                            >
                                {getLastJob()?.jobTitle}
                            </span>
                            <span
                                title={getLastJob()?.currentJob ? getLastJob().employerName : ''}
                                className="line-clamp-1 text-sm md:text-2xl"
                            >
                                {getLastJob()?.currentJob ? getLastJob().employerName : ''}
                            </span>
                            <span className="text-sm md:text-xl">
                                {user.livesAbroad ? 'Viviendo en el extranjero' : user.nationality}
                            </span>
                        </div>
                    </div>
                    <div className="flex flex-col justify-center gap-1 md:gap-4 h-full w-full md:w-[50%] max-w-[200px] md:max-w-[400px]">
                        <span className="font-semibold text-base md:text-2xl">
                            Información de contacto
                        </span>
                        <div className="flex flex-col md:gap-3 text-base md:text-xl">
                            <span
                                title={user.email}
                                className="break-words line-clamp-2 text-xs md:text-xl"
                            >
                                Email: {user.email}
                            </span>
                            {user.urlUserLinkedIn && (
                                <div className="flex gap-1 md:gap-2 items-center">
                                    <span className="text-xs md:text-xl mb-1 md:mb-0">
                                        Contacto:
                                    </span>
                                    <a
                                        href={`${user.urlUserLinkedIn.startsWith('http') ? '' : '///'}${user.urlUserLinkedIn}`}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="hover:bg-transparent p-0 text-linkedin"
                                    >
                                        <LinkedInIcon fontSize={smallScreen ? 'small' : 'large'} />
                                    </a>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            {!smallScreen
                ? !isAdmin() && (
                      <div className="pr-2 md:pr-5 pt-2 md:pt-5 w-fit">
                          {self ? (
                              <div className="flex flex-col gap-1 md:gap-2">
                                  <CustomButton
                                      startIcon={<EditIcon />}
                                      text="Actualizar"
                                      onClick={() => setIsUpdateInfoOpen(true)}
                                      secondary
                                  />
                                  <PDFDownloadLink
                                      document={<PDF user={user} />}
                                      fileName="profile.pdf"
                                  >
                                      <CustomButton
                                          startIcon={<FileDownloadIcon />}
                                          text="Descargar"
                                          secondary
                                      />
                                  </PDFDownloadLink>
                              </div>
                          ) : (
                              <CustomButton
                                  startIcon={<EmailIcon />}
                                  onClick={() =>
                                      navigate(AppRoutes.messages.replace(':userId', user.id), {
                                          state: { messageTo: user },
                                      })
                                  }
                                  secondary
                              >
                                  <span className="whitespace-nowrap text-xs md:text-base">
                                      Enviar mensaje
                                  </span>
                              </CustomButton>
                          )}
                      </div>
                  )
                : null}
            <CustomModal
                isOpen={isUpdateInfoOpen}
                handleClose={() => setIsUpdateInfoOpen(false)}
            >
                <UpdateUserInfo
                    user={user}
                    handleClose={() => setIsUpdateInfoOpen(false)}
                    config={config}
                />
            </CustomModal>
        </CustomPaper>
    )
}

export default UserBasicInfo
